import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";

export default function AdminPackages() {
  const [packages, setPackages] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ price_inr: 99, coins: 1000, bonus_percent: 0, popular: false, active: true });

  const load = () => adminApi.get("/admin/packages").then(({ data }) => setPackages(data.packages)).catch(() => setPackages([]));
  useEffect(() => { load(); }, []);

  const save = async (pkg, updates) => {
    try {
      await adminApi.put(`/admin/packages/${pkg.id}`, { ...pkg, ...updates });
      load();
      toast.success("Package updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/admin/packages", { ...form, price_inr: Number(form.price_inr), coins: Number(form.coins), bonus_percent: Number(form.bonus_percent) });
      setShowForm(false);
      load();
      toast.success("Package created");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-extrabold font-display">Coin Packages</h1>
        <button data-testid="new-package-button" onClick={() => setShowForm(!showForm)}
          className="gradient-primary text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-1.5"><Plus size={14} /> New Package</button>
      </div>

      {showForm && (
        <form onSubmit={create} className="glass rounded-2xl p-4 mb-4 flex flex-wrap items-end gap-3">
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Price (₹)</label>
            <input data-testid="package-price-input" type="number" value={form.price_inr} onChange={(e) => setForm((f) => ({ ...f, price_inr: e.target.value }))} className="w-24 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" /></div>
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Coins</label>
            <input data-testid="package-coins-input" type="number" value={form.coins} onChange={(e) => setForm((f) => ({ ...f, coins: e.target.value }))} className="w-28 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" /></div>
          <div><label className="text-[10px] text-zinc-500 font-semibold block mb-1">Bonus %</label>
            <input data-testid="package-bonus-input" type="number" value={form.bonus_percent} onChange={(e) => setForm((f) => ({ ...f, bonus_percent: e.target.value }))} className="w-20 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" /></div>
          <label className="flex items-center gap-1.5 text-xs text-zinc-400 pb-2">
            <input type="checkbox" checked={form.popular} onChange={(e) => setForm((f) => ({ ...f, popular: e.target.checked }))} /> Most Popular
          </label>
          <button data-testid="package-create-submit" className="gradient-primary text-white text-xs font-bold px-5 py-2.5 rounded-xl">Create</button>
        </form>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="p-3">Base Price</th><th>18% GST</th><th>Total Payable</th><th>Coins</th><th>Bonus</th><th>Popular</th><th>Status</th></tr></thead>
          <tbody>
            {packages?.map((p) => (
              <tr key={p.id} className="border-b border-white/5" data-testid={`package-row-${p.id}`}>
                <td className="p-3 text-white font-bold">₹{p.price_inr.toLocaleString()}</td>
                <td className="text-amber-300">₹{(p.price_inr * 0.18).toFixed(2)}</td>
                <td className="text-emerald-400 font-extrabold">₹{(p.price_inr * 1.18).toFixed(2)}</td>
                <td className="text-amber-400 font-bold">{p.coins.toLocaleString()}</td>
                <td className="text-emerald-400">{p.bonus_percent}%</td>
                <td>
                  <button data-testid={`package-popular-${p.id}`} onClick={() => save(p, { popular: !p.popular })}
                    className={`px-2.5 py-1 rounded-lg font-semibold ${p.popular ? "gradient-primary text-white" : "bg-white/10 text-zinc-400"}`}>
                    {p.popular ? "★ Popular" : "Mark"}
                  </button>
                </td>
                <td>
                  <button data-testid={`package-toggle-${p.id}`} onClick={() => save(p, { active: !p.active })}
                    className={`px-2.5 py-1 rounded-lg font-semibold ${p.active ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
